import React, { useEffect, useRef, useState } from 'react';
import Breadcrumb       from '@/components/common/Breadcrumb';
import CustomDropdown   from '@/components/common/CustomDropdown';
import Card             from '@/components/ui/cards/Card';
import CardHeader       from '@/components/ui/cards/CardHeader';
import { cn }           from '@/lib/utils';
import { useDispatch }  from 'react-redux';
import { getSalesReportService } from '@/services/ReportServices';
import validationErrors from '@/lib/validationErrors';
import DataTableFilter  from './component/DataTableFilter';
import DataTable        from './component/DataTable';
import PritntPdfTable   from '@/components/common/PritntPdfTable';   // keep existing spelling

const SalesReportsPage = ({ pageTitle }) => {
  const dispatch = useDispatch();
  const { printTableRef, handleDownloadPdf } = PritntPdfTable('Sales Report');

  /* ──────────────────── state ──────────────────── */
  const [dataSource, setDataSource] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [summary, setSummary] = useState({
    totalOrders:     0,
    totalEarnings:   0,
    totalDiscounts:  0,
    totalDelivery:   0,
  });
  const [isLoading,   setIsLoading]   = useState(false);
  const [isFilterOpen,setIsFilterOpen]= useState(false);
  const filterDivRef  = useRef(null);
  const [dataFilter,  setDataFilter]  = useState({
    search: '',
    page:   1,
    limit:  10,
    extra_filter: {},
  });

  /* ──────────────────── fetch data ──────────────────── */
  useEffect(() => {
    setIsLoading(true);

    dispatch(getSalesReportService(dataFilter))
      .then(res => {
        const { data: rows = [], total = 0 } = res.data.data;
        setDataSource(rows);
        setTotalRecord(total);

        const earnings   = rows.reduce((s, r) => s + (+r.total           || 0), 0);
        const discounts  = rows.reduce((s, r) => s + (+r.discount        || 0), 0);
        const deliveries = rows.reduce((s, r) => s + (+r.delivery_charge || 0), 0);

        setSummary({
          totalOrders:   total,
          totalEarnings: earnings,
          totalDiscounts: discounts,
          totalDelivery: deliveries,
        });
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  }, [dispatch, dataFilter]);

  /* ──────────────────── handlers ──────────────────── */
  const toggleFilter = () => {
    if (!filterDivRef.current) return;
    filterDivRef.current.style.height = isFilterOpen ? '0px' : 'auto';
    setIsFilterOpen(!isFilterOpen);
  };

  const onChangeFilter = (name, value) => {
    if (name === 'extra_filter') {
      setDataFilter(prev => ({
        ...prev,
        extra_filter: { ...value, tableOrder: '' },
      }));
    } else {
      setDataFilter(prev => ({ ...prev, [name]: value }));
    }
  };

  /* ──────────────────── table columns ──────────────────── */
  const tableColumns = [
    { name: 'Order Id',        isIncludePrint: true },
    { name: 'Date',            isIncludePrint: true },
    { name: 'Items',           isIncludePrint: true },
    { name: 'Total',           isIncludePrint: true },
    { name: 'Discount',        isIncludePrint: true },
    { name: 'Delivery Charge', isIncludePrint: true },
    { name: 'Payment Type',    isIncludePrint: true },
    { name: 'Payment Status',  isIncludePrint: true },
  ];

  return (
    <>
      <Breadcrumb pageTitle={pageTitle} />

      <Card>
        {/* ───── header with Filter / Export ───── */}
        <CardHeader>
          <h3 className="db-card-title">{pageTitle}</h3>
          <div className="db-card-filter">
            <button
              className="db-card-filter-btn table-filter-btn"
              onClick={toggleFilter}
            >
              <i className="lab lab-total-orders text-gray-500" />
              <span>Filter</span>
            </button>

            <CustomDropdown
              buttonText="Export"
              buttonIcon="lab-file-export"
              dropdownList={[
                { icon: 'lab-printer-line', text: 'Print', dropDownAction: handleDownloadPdf },
              ]}
            />
          </div>
        </CardHeader>

        {/* ───── summary cards exactly like mock-up ───── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 px-6 mb-6">
          {/* Total Orders */}
          <Card className="flex items-center p-5">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 mr-4">
             <i className="lab lab-sale-summary text-gray-500" />
            </span>
            <div>
              <p className="text-sm text-gray-500 mb-0.5">Total Orders</p>
              <p className="text-lg font-semibold">{summary.totalOrders}</p>
            </div>
          </Card>

          {/* Total Earnings */}
          <Card className="flex items-center p-5">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 mr-4">
            <i className="lab lab-discount-shape text-gray-500" />
            </span>
            <div>
              <p className="text-sm text-gray-500 mb-0.5">Total Earnings</p>
              <p className="text-lg font-semibold">${summary.totalEarnings.toFixed(2)}</p>
            </div>
          </Card>

          {/* Total Discounts */}
          <Card className="flex items-center p-5">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 mr-4">
      <i className="lab lab-printer text-gray-500" />
            </span>
            <div>
              <p className="text-sm text-gray-500 mb-0.5">Total Discounts</p>
              <p className="text-lg font-semibold">${summary.totalDiscounts.toFixed(2)}</p>
            </div>
          </Card>

          {/* Total Delivery Charges */}
          <Card className="flex items-center p-5">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 mr-4">
             <i className="lab lab-printer lab-font-size-18 text-gray-500" />
            </span>
            <div>
              <p className="text-sm text-gray-500 mb-0.5">Total Delivery Charges</p>
              <p className="text-lg font-semibold">${summary.totalDelivery.toFixed(2)}</p>
            </div>
          </Card>
        </div>

        {/* ───── collapsible filter drawer ───── */}
        <div
          ref={filterDivRef}
          className={cn(
            'table-filter-div overflow-hidden transition-height',
            isFilterOpen && 'active'
          )}
        >
          <DataTableFilter onChangeFilter={onChangeFilter} />
        </div>

        {/* ───── data table + print ref ───── */}
        <div ref={printTableRef}>
          <DataTable
            isLoading={isLoading}
            tableColumn={tableColumns}
            dataSource={dataSource}
            totalRecord={totalRecord}
            dataFilter={dataFilter}
            onChangePage={onChangeFilter}
          />
        </div>
      </Card>
    </>
  );
};

export default SalesReportsPage;